import { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase-config';
import { Button } from '../ui/button';
import { Mail, MessageSquare, Save, Loader2, CheckCircle2, Send, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

interface IntegrationsSettingsProps {
  customerId: string;
  brandColor: string;
}

interface CustomerData {
  sendgridApiKey?: string;
  sendgridFromEmail?: string;
  sendgridFromName?: string;
  slackWebhookUrl?: string;
}

export default function IntegrationsSettings({ customerId, brandColor }: IntegrationsSettingsProps) {
  const [customerData, setCustomerData] = useState<CustomerData>({});
  const [loading, setLoading] = useState(true);

  // SendGrid State
  const [sendgridApiKey, setSendgridApiKey] = useState('');
  const [sendgridFromEmail, setSendgridFromEmail] = useState('');
  const [sendgridFromName, setSendgridFromName] = useState('');
  const [savingSendgrid, setSavingSendgrid] = useState(false);

  // Slack State
  const [slackWebhookUrl, setSlackWebhookUrl] = useState('');
  const [savingSlack, setSavingSlack] = useState(false);
  const [testingSlack, setTestingSlack] = useState(false);

  useEffect(() => {
    fetchCustomerData();
  }, [customerId]);

  const fetchCustomerData = async () => {
    try {
      const customerRef = doc(db, 'customers', customerId);
      const customerSnap = await getDoc(customerRef);

      if (customerSnap.exists()) {
        const data = customerSnap.data() as CustomerData;
        setCustomerData(data);

        // Populate form fields
        setSendgridApiKey(data.sendgridApiKey || '');
        setSendgridFromEmail(data.sendgridFromEmail || '');
        setSendgridFromName(data.sendgridFromName || '');
        setSlackWebhookUrl(data.slackWebhookUrl || '');
      }
    } catch (error) {
      console.error('Error fetching customer data:', error);
      toast.error('Failed to load integration settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSendGrid = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingSendgrid(true);

    try {
      const customerRef = doc(db, 'customers', customerId);
      await updateDoc(customerRef, {
        sendgridApiKey: sendgridApiKey.trim() || null,
        sendgridFromEmail: sendgridFromEmail.trim() || null,
        sendgridFromName: sendgridFromName.trim() || null,
        updatedAt: new Date(),
      });

      toast.success('SendGrid settings saved successfully!');
      await fetchCustomerData();
    } catch (error) {
      console.error('Error saving SendGrid settings:', error);
      toast.error('Failed to save SendGrid settings');
    } finally {
      setSavingSendgrid(false);
    }
  };

  const handleSaveSlack = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingSlack(true);

    try {
      const customerRef = doc(db, 'customers', customerId);
      await updateDoc(customerRef, {
        slackWebhookUrl: slackWebhookUrl.trim() || null,
        updatedAt: new Date(),
      });

      toast.success('Slack settings saved successfully!');
      await fetchCustomerData();
    } catch (error) {
      console.error('Error saving Slack settings:', error);
      toast.error('Failed to save Slack settings');
    } finally {
      setSavingSlack(false);
    }
  };

  const handleTestSlack = async () => {
    if (!slackWebhookUrl.trim()) {
      toast.error('Please enter a Slack webhook URL first');
      return;
    }

    setTestingSlack(true);

    try {
      // Call the Cloud Function to test the webhook (avoids CORS issues)
      const functionUrl = import.meta.env.VITE_FIREBASE_FUNCTIONS_URL ||
        'https://us-central1-productworkshop-dev.cloudfunctions.net';

      const response = await fetch(`${functionUrl}/testSlackWebhook`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          webhookUrl: slackWebhookUrl,
        }),
      });

      const result = await response.json();

      if (result.success) {
        toast.success('Test message sent! Check your Slack channel.');
      } else {
        toast.error(`Failed to send test message: ${result.error}`);
      }
    } catch (error: any) {
      console.error('Error testing Slack webhook:', error);
      toast.error(`Failed to send test message: ${error.message}`);
    } finally {
      setTestingSlack(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground">Integrations</h2>
        <p className="text-muted-foreground mt-1">
          Configure third-party integrations to enhance your feedback portal
        </p>
      </div>

      {/* Slack Integration */}
      <div className="border border-border rounded-lg bg-card">
        <div className="p-6 border-b border-border">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-lg bg-purple-100 dark:bg-purple-900/20 flex items-center justify-center flex-shrink-0">
              <MessageSquare className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-foreground">Slack Notifications</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Receive real-time notifications in Slack when customers submit new feature requests
              </p>
              {customerData.slackWebhookUrl && (
                <div className="flex items-center gap-2 mt-2">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <span className="text-sm text-green-600 dark:text-green-400 font-medium">
                    Active
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        <form onSubmit={handleSaveSlack} className="p-6 space-y-4">
          {/* Info Banner */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-400 mb-2">
              What you'll get:
            </h4>
            <ul className="text-sm text-blue-800 dark:text-blue-300 list-disc list-inside space-y-1">
              <li>Real-time notifications when new requests are submitted</li>
              <li>Rich message formatting with request details</li>
              <li>Direct links to view and manage requests in your admin panel</li>
              <li>Information about who submitted the request and why</li>
            </ul>
          </div>

          {/* Webhook URL Input */}
          <div>
            <label htmlFor="slackWebhookUrl" className="block text-sm font-medium text-foreground mb-2">
              Slack Webhook URL
            </label>
            <input
              type="url"
              id="slackWebhookUrl"
              value={slackWebhookUrl}
              onChange={(e) => setSlackWebhookUrl(e.target.value)}
              placeholder="https://hooks.slack.com/services/YOUR/WEBHOOK/URL"
              className="w-full px-3 py-2 rounded-md border border-input bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary font-mono text-sm"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Get your webhook URL from{' '}
              <a
                href="https://api.slack.com/messaging/webhooks"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline inline-flex items-center gap-1"
              >
                Slack's Incoming Webhooks <ExternalLink className="h-3 w-3" />
              </a>
            </p>
          </div>

          {/* Setup Instructions */}
          <details className="border border-border rounded-lg">
            <summary className="px-4 py-3 cursor-pointer text-sm font-medium text-foreground hover:bg-muted rounded-lg">
              📖 Slack Setup Instructions
            </summary>
            <div className="px-4 pb-4 space-y-3 text-sm text-muted-foreground">
              <div>
                <p className="font-medium text-foreground mb-1">1. Create a Slack App</p>
                <p>Visit <a href="https://api.slack.com/apps" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">api.slack.com/apps</a> and click "Create New App" → "From scratch"</p>
              </div>
              <div>
                <p className="font-medium text-foreground mb-1">2. Enable Incoming Webhooks</p>
                <p>In your app settings, go to "Incoming Webhooks" and toggle it ON</p>
              </div>
              <div>
                <p className="font-medium text-foreground mb-1">3. Add Webhook to Workspace</p>
                <p>Click "Add New Webhook to Workspace" and select the channel for notifications</p>
              </div>
              <div>
                <p className="font-medium text-foreground mb-1">4. Copy the Webhook URL</p>
                <p>Copy the webhook URL and paste it in the field above</p>
              </div>
            </div>
          </details>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleTestSlack}
              disabled={testingSlack || !slackWebhookUrl.trim()}
            >
              {testingSlack ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Test Connection
                </>
              )}
            </Button>
            <Button
              type="submit"
              disabled={savingSlack}
              style={{ backgroundColor: brandColor }}
              className="text-white"
            >
              {savingSlack ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Settings
                </>
              )}
            </Button>
          </div>
        </form>
      </div>

      {/* SendGrid Integration */}
      <div className="border border-border rounded-lg bg-card">
        <div className="p-6 border-b border-border">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-lg bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center flex-shrink-0">
              <Mail className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-foreground">SendGrid Email</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Send email notifications from your own domain using SendGrid
              </p>
              {customerData.sendgridApiKey && (
                <div className="flex items-center gap-2 mt-2">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <span className="text-sm text-green-600 dark:text-green-400 font-medium">
                    Active
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        <form onSubmit={handleSaveSendGrid} className="p-6 space-y-4">
          {/* Info Banner */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-400 mb-2">
              Why configure SendGrid?
            </h4>
            <ul className="text-sm text-blue-800 dark:text-blue-300 list-disc list-inside space-y-1">
              <li>Send emails from your own domain (e.g., notifications@yourdomain.com)</li>
              <li>Have full control over email delivery and analytics</li>
              <li>Avoid shared sender reputation issues</li>
            </ul>
          </div>

          {/* API Key */}
          <div>
            <label htmlFor="sendgridApiKey" className="block text-sm font-medium text-foreground mb-2">
              SendGrid API Key
            </label>
            <input
              type="password"
              id="sendgridApiKey"
              value={sendgridApiKey}
              onChange={(e) => setSendgridApiKey(e.target.value)}
              placeholder="SG.xxxxxxxxxxxx"
              className="w-full px-3 py-2 rounded-md border border-input bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary font-mono text-sm"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Get your API key from{' '}
              <a
                href="https://app.sendgrid.com/settings/api_keys"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline inline-flex items-center gap-1"
              >
                SendGrid Dashboard <ExternalLink className="h-3 w-3" />
              </a>
            </p>
          </div>

          {/* From Email */}
          <div>
            <label htmlFor="sendgridFromEmail" className="block text-sm font-medium text-foreground mb-2">
              From Email Address
            </label>
            <input
              type="email"
              id="sendgridFromEmail"
              value={sendgridFromEmail}
              onChange={(e) => setSendgridFromEmail(e.target.value)}
              placeholder="notifications@yourdomain.com"
              className="w-full px-3 py-2 rounded-md border border-input bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              This email must be verified in SendGrid
            </p>
          </div>

          {/* From Name */}
          <div>
            <label htmlFor="sendgridFromName" className="block text-sm font-medium text-foreground mb-2">
              From Name
            </label>
            <input
              type="text"
              id="sendgridFromName"
              value={sendgridFromName}
              onChange={(e) => setSendgridFromName(e.target.value)}
              placeholder="Your Company Name"
              className="w-full px-3 py-2 rounded-md border border-input bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              The display name that appears in recipient inboxes
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="submit"
              disabled={savingSendgrid}
              style={{ backgroundColor: brandColor }}
              className="text-white"
            >
              {savingSendgrid ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Settings
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
